import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { throttle, get } from 'lodash';
import classNames from 'classnames';
import { gtm } from 'apps/gtm';

import { config } from 'api';

import { dynamicYieldService } from 'apps/dynamicYield';

import QuantityControls from '../QuantityControls';
import Locations from 'apps/basket/models/Locations';
import StickyNavigation from './StickyNavigation';
import { setErrorMessage } from 'apps/notifications/actions/notifications';
import { changeBasketQuantity, clearProductWarning } from 'apps/basket/actions/basket';
import { requestLogin } from 'utils/loginAndRegistration/login';
import { wa } from 'apps/analytics';
import { pageEvents } from 'apps/products/analytics';
import { fetchRecommendations, ensureProductDetails } from '../../actions/productDetails';

export default class BasketControls extends PureComponent {
  static propTypes = {
    analyticsContextData: PropTypes.object,
    sku: PropTypes.string,
    dispatch: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    isUpdating: PropTypes.bool,
    isLoggedIn: PropTypes.bool,
    loggedOutBasket: PropTypes.bool,
    maxQuantity: PropTypes.number,
    minQuantity: PropTypes.number,
    quantity: PropTypes.number.isRequired,
    router: PropTypes.object.isRequired,
    warnings: PropTypes.object,
    className: PropTypes.string,
    classNamePrefix: PropTypes.string,
    showClearButton: PropTypes.bool,
    betaFeatures: PropTypes.array.isRequired,
    productDetails: PropTypes.object,
    isFlashSale: PropTypes.bool,
  };

  static defaultProps = {
    warnings: {},
    className: '',
    classNamePrefix: 'basketControls',
    showClearButton: false,
  };

  state = {
    stickyVisible: false,
    isDesktop: false,
    headerHeight: 0,
  }

  componentDidMount() {
    document.addEventListener('scroll', this.updateStickyVisibility);
    this.mq = window.matchMedia(`(min-width: ${config.breakpoints.medium_large})`);
    this.mq.addListener(this.handleMediaQuery);
    this.handleMediaQuery(this.mq);
  }

  componentDidUpdate() {
    // React does not see the difference between 00002 and 2, therefore we need to update the value ourselves
    if (this.quantityInput) {
      const { quantity } = this.props;
      this.quantityInput.value = quantity && quantity.length ? parseInt(quantity, 10) : quantity;
    }
  }

  componentWillUnmount() {
    document.removeEventListener('scroll', this.updateStickyVisibility);
  }

  stickyControlsRef = React.createRef();

  staticControlsRef = React.createRef();

  updateStickyVisibility = throttle(() => {
    const staticControlsBox = get(this, 'staticControlsRef.current', false);
    if (staticControlsBox) {
      const staticControlsBoxCoordinates = staticControlsBox.getBoundingClientRect();

      const stickyHeader = document.querySelector('.rs-stickyHeader');
      const headerHeight = stickyHeader ? stickyHeader.getBoundingClientRect().height : 0;

      const stickyVisible = staticControlsBoxCoordinates.top < (headerHeight - staticControlsBoxCoordinates.height);

      if (stickyVisible !== this.state.stickyVisible) {
        this.clearWarning(this.props.sku);

        if (stickyVisible) {
          wa.triggerPageEventWithDescription(pageEvents.BOP_STICKY_SHOWN);
        }
      }

      document.querySelector('#root-view').classList.toggle('bop-stickyBasketControls--isVisible', stickyVisible);

      this.setState({
        stickyVisible,
        headerHeight,
      });
    }
  }, 50, { leading: true });

  onQuantitySubmit = (quantityDelta) => {
    const {
      sku,
      analyticsContextData,
      isFlashSale,
      productDetails,
    } = this.props;

    if (!sku) {
      return this.props.dispatch(setErrorMessage('Invalid sku'));
    }

    const productData = {
      gtmPageType: 'Back of Pack',
      product: analyticsContextData.product,
      productDetails,
      basket: get(analyticsContextData, 'basket'),
      quantity: Math.abs(quantityDelta),
      section: 'Back of Pack',
      cartActionType: 'BOP',
    };

    dynamicYieldService.reportCartEvent({
      quantityDelta, ...analyticsContextData,
    });
    dynamicYieldService.reportSyncCartEvent({ basketItems: get(analyticsContextData, 'basket.items') });
    gtm.trigger(quantityDelta > 0 ? 'gtm:addToTrolley' : 'gtm:removeFromTrolley', productData);

    return this.props.dispatch(changeBasketQuantity({
      items: { [sku]: quantityDelta },
      location: isFlashSale ? Locations.CHECKOUTPURGE : Locations.BOP,
      analyticsContextData,
    })).then(() => {
      if (quantityDelta > 0) {
        this.props.dispatch(ensureProductDetails(sku));
        this.props.dispatch(fetchRecommendations(sku));
      }
    });
  };

  getWarning = () => {
    const warnings = Object.keys(this.props.warnings)
      .map(warningKey => this.props.warnings[warningKey]);

    return warnings.length
      ? `You've added the maximum amount to this order. ${warnings.join()}`
      : '';
  };

  handleMediaQuery = (mq) => {
    this.setState({ isDesktop: mq.matches });
  };

  handleLoggedOut = () => {
    const { router } = this.props;
    requestLogin(router);
  };

  beforeChange = () => {
    const {
      loggedOutBasket,
      isLoggedIn,
    } = this.props;

    if (!loggedOutBasket && !isLoggedIn) {
      this.handleLoggedOut();
      return false;
    }
    return true;
  };

  clearWarning = () => {
    const { sku } = this.props;
    this.props.dispatch(clearProductWarning(sku));
  }

  renderQuantityControls = (controlsPosition) => {
    const { stickyVisible, isDesktop } = this.state;
    const controlsConfig = {
      sticky: {
        wrapperRef: this.stickyControlsRef,
        tooltipForcePosition: isDesktop ? ['below', 'center'] : ['above', 'center'],
      },
      static: {
        wrapperRef: this.staticControlsRef,
        className: stickyVisible ? 'bop-basketControls--stickyVisible' : '',
        tooltipForcePosition: ['below', 'center'],
      },
    };
    const {
      disabled,
      quantity,
      isUpdating,
      minQuantity,
      maxQuantity,
      className: propsClassName,
      classNamePrefix,
      showClearButton,
    } = this.props;

    const {
      wrapperRef,
      className: configClassName,
      tooltipForcePosition,
    } = controlsConfig[controlsPosition];

    return (
      <QuantityControls
        classNamePrefix={classNamePrefix}
        className={classNames(propsClassName, configClassName)}
        disabled={disabled}
        quantity={quantity}
        onUpdate={this.onQuantitySubmit}
        beforeChange={this.beforeChange}
        warning={this.getWarning()}
        isUpdating={isUpdating}
        maxQuantity={maxQuantity}
        minQuantity={minQuantity}
        onWarning={() => wa.triggerPageEventWithDescription(pageEvents.BOP_QUANTITY_RESTRICTION_EXCEEDED)}
        addButtonLabel="Add to trolley"
        showClearButton={showClearButton}
        wrapperRef={wrapperRef}
        clearWarning={this.clearWarning}
        tooltipForcePosition={tooltipForcePosition}
      />
    );
  };

  render() {
    const {
      betaFeatures,
      productDetails,
      quantity,
    } = this.props;
    const {
      stickyVisible,
      headerHeight,
      isDesktop,
    } = this.state;

    const {
      showShadow = true,
      showRecommendations = true,
    } = get(config, 'bop.basketControls', {});

    return (
      <>
        {this.renderQuantityControls('static')}
        <div
          className={classNames(
            'bop-basketControls__sticky', {
              'bop-basketControls__sticky--isVisible': stickyVisible,
              'bop-basketControls__sticky--desktopMode': isDesktop,
              'bop-basketControls__sticky--noShadow': !showShadow,
            }
          )}
          style={isDesktop && stickyVisible ? { top: `${headerHeight}px` } : {}}
        >
          <div className="bop-basketControls__stickyWrapper">
            <StickyNavigation
              betaFeatures={betaFeatures}
              isInBasket={quantity > 0}
              productDetails={productDetails}
              showRecommendations={showRecommendations}
            />
            {this.renderQuantityControls('sticky')}
          </div>
        </div>
      </>
    );
  }
}
